import { DestroyRef, inject, Injectable, Injector } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { AngularFireAnalytics } from "@angular/fire/compat/analytics";
import { doc, Firestore, getDoc, onSnapshot } from "@angular/fire/firestore";
import { MatSnackBar } from "@angular/material/snack-bar";
import { ActivatedRoute, Data, NavigationEnd, Router } from "@angular/router";
import { AuthService } from "@core/auth.service";
import { AuthPartialRegService } from "@core/AuthServices/authPartialReg/auth-partial-reg.service";
import { CatchErrorService } from "@core/catch-error.service";
import { LoadingService } from "@core/loading.service";
import * as CryptoJS from "crypto-js";
import { getAuth } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import { BehaviorSubject, defer, from, Observable, of, throwError } from "rxjs";
import { catchError, delay, filter, map, retryWhen, scan, shareReplay, switchMap, take } from "rxjs/operators";
import { CommonVariable } from "../app.constants";
import { MyToastbarComponent } from "../components/my-toastbar/my-toastbar.component";
import { ClevertapService } from "./clevertap.service";
import { ClarityService } from "./clarity.service";
import { CommonService } from "./common/common.service";
import { PosthogService } from "./posthog.service";
import {
  buildPaymentSucceededProps,
  firstPresentString,
  firstPurchaseAmount,
  mergePaymentPurchaseContext,
  PaymentPurchaseContext,
  resolvePaymentAmountAndOffer,
} from "./posthog-payment-props.helper";
import { SafeLocalStorageService } from "./safe-local-storage.service";
declare let fbq: any;
declare let dataLayer;
declare let analytics;
declare var window;
@Injectable({ providedIn: "root" })
export class GTMSagmentService {
  public static readonly FIREBASE_REGION = "europe-west3";
  private isInLoginFlow = false;
  // LOTTOSOCIAL-PWA-1NB6: single source of truth for the partial-registration route segment.
  // Using a constant avoids divergence between the guard and the existing includes() checks below.
  private readonly PARTIAL_REG_ROUTE = 'partial-registration';
  // Tracks the Prosub_ID for which safe prize:0 defaults were last pushed. `undefined` means
  // defaults have never been pushed for the current route visit. Tracking the ID (not just a
  // boolean) ensures defaults are re-pushed when Prosub_ID changes on the same route, so GTM
  // tags never see stale values during the Firestore fetch for the new product.
  // Reset to undefined via NavigationEnd when the user successfully leaves the route.
  private lastPartialRegDefaultsProsubId: string | null | undefined = undefined;
  purchaseTagDetails$ = new BehaviorSubject<any>({});
  userIsNew: boolean = false;
  prosubDetails: any;
  prosub: any;
  userProduct$ = new BehaviorSubject<any>({});
  vc: string;
  queryParamsToNavigate: any;
  translatedToastMsg: any;
  selectedLanguage: string = CommonVariable.preferredLanguage;
  private safeLocalStorage = inject(SafeLocalStorageService);
  private destroyRef = inject(DestroyRef);
  private authService = inject(AuthService);
  private posthogService = inject(PosthogService);
  selectedlanguage$ = new BehaviorSubject(this.safeLocalStorage.getItem("language_code") || "");

  public TP1: string = this.safeLocalStorage.getItem("TP1");
  public TP2: string = this.safeLocalStorage.getItem("TP2");
  public TP3: string = this.safeLocalStorage.getItem("TP3");
  public T_ID: string = this.safeLocalStorage.getItem("T_ID");
  constructor(
    private router: Router,
    private snackBar: MatSnackBar,
    private analyticsFire: AngularFireAnalytics,
    private actRoute: ActivatedRoute,
    private firestore: Firestore,
    private loading: LoadingService,
    private injector: Injector,
    private catchService: CatchErrorService,
    private AuthPartialRegService: AuthPartialRegService,
    private commonService: CommonService,
    private clevertapService: ClevertapService,
    private clarityService: ClarityService
  ) {
    this.commonService.getUid("gtm_sagment_service").pipe().subscribe({
      next: (uid) => {
        this.selectedLanguage = uid
          ? localStorage.getItem("language_code")
          : CommonVariable.preferredLanguage;

      }
    });

    // LOTTOSOCIAL-PWA-1NB6: reset the prosubId tracker when the user successfully leaves
    // partial-registration, so re-entering the route always pushes fresh safe defaults.
    // NavigationEnd (not NavigationStart) is used so cancelled/redirected navigations do
    // not incorrectly reset the tracker before the user has actually left.
    // The type predicate ensures correct narrowing regardless of RxJS/TS version.
    // Path segments are matched with a regex so substring false-positives (e.g.
    // /foo-partial-registration-bar) cannot prevent the reset.
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(event => {
        if (!this.isPartialRegUrl(event.urlAfterRedirects)) {
          this.lastPartialRegDefaultsProsubId = undefined;
        }
      });
  }

  /**
   * Returns true when `url` resolves to the /partial-registration route.
   * Strips query string and fragment before matching so that URLs like
   * /login?returnUrl=/partial-registration do not produce false positives.
   * Uses a segment-boundary regex built from PARTIAL_REG_ROUTE so the
   * constant is the single source of truth.
   */
  private isPartialRegUrl(url: string): boolean {
    const path = url.split('?')[0].split('#')[0];
    return new RegExp(`(^|/)${this.PARTIAL_REG_ROUTE}(/|;|$)`).test(path);
  }

  GTMInitilization(): void {
    const pageName = this.router.url.split('/').pop() ?? '';

    // Analytics event tracking
    if (typeof analytics !== "undefined") {
      analytics.track("Page_Events", {
        name: pageName,
        path: window.location.pathname,
        title: document.title,
        url: window.location.href,
      });
    }

    this.analyticsFire.logEvent("Page_Events", { pageName: this.router.url });

    // If payment success redirected to 4C/profile before GTM consumed the event, replay once.
    this.replayPendingPaymentSuccessIfNeeded();

    // Check if URL matches specific patterns
    const matchingRoutes = [
      "partial-registration",
      "account-created",
      "successful-payment",
      "free-reg-complete-profile",
      "free-reg-step4c",
    ];

    const isMatchingRoute = matchingRoutes.some(route => this.router.url.includes(route));

    if (!isMatchingRoute) return;

    // Fetch query params and handle `prosub`
    this.actRoute.queryParamMap.pipe(take(1)).subscribe(params => {
      const prosubIdFromUrl = new URLSearchParams(window.location.search).get("Prosub_ID")
        || new URLSearchParams(window.location.search).get("prosub_id");
      const prosubId = params.get("Prosub_ID") || params.get("prosub_id") || prosubIdFromUrl;

      // LOTTOSOCIAL-PWA-1NB6 / LOTTOSOCIAL-PWA-1NAJ: push safe dataLayer defaults once per
      // partial-registration route entry (and again when Prosub_ID changes) so GTM tags that
      // fire before Firestore returns always see a numeric prize and a non-null Prosub_Id.
      // lastPartialRegDefaultsProsubId is reset by the NavigationEnd subscription in the
      // constructor whenever the user successfully leaves this route, ensuring fresh defaults
      // on every re-entry without overwriting already-loaded Firestore data on repeated
      // GTMInitilization() calls. isPartialRegUrl() is the single route-matching helper.
      const isPartialRegRoute = this.isPartialRegUrl(this.router.url);

      // LOTTOSOCIAL-PWA-1NB6 / LOTTOSOCIAL-PWA-1NAJ: push safe defaults once per
      // partial-registration route entry, and again whenever Prosub_ID changes, so GTM
      // tags always see a numeric prize and valid Prosub_Id before Firestore returns.
      // lastPartialRegDefaultsProsubId tracks the ID (not just a boolean) so a Prosub_ID
      // change within the same route visit triggers a fresh placeholder push.
      const needsDefaultPush = isPartialRegRoute &&
        this.lastPartialRegDefaultsProsubId !== (prosubId ?? null);

      if (needsDefaultPush) {
        // Always set Prosub_Id (including null) so a missing ID clears any stale value
        // left in the dataLayer from a prior product on this route.
        dataLayer.push({
          Prosub_Id: prosubId,
          prosub_price: 0,
          prize: 0,
          // prosub_price_valid: false signals that real data has not yet loaded.
          prosub_price_valid: false,
        });
        // Set tracker after push so an error does not silently suppress future retries.
        this.lastPartialRegDefaultsProsubId = prosubId ?? null;
      }

      if (prosubId) {
        this.prosub = { ...params };

        // Handle partial-registration route — trigger Firestore fetch to overwrite the
        // safe defaults above with real prosub data. Intentionally not guarded so real
        // data always wins regardless of how many times GTMInitilization is called.
        if (isPartialRegRoute) {
          const requestedProsubId = prosubId;
          this.getLSWProsubDetails(requestedProsubId)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(
              (prosubDetails: Data) => {
                // Drop stale responses when Prosub_ID changed (or the user left the route)
                // while this request was in flight.
                if (this.lastPartialRegDefaultsProsubId !== requestedProsubId) {
                  return;
                }
                this.prosubDetails = prosubDetails?.data();
                // Match gtm-segment.service.ts: reject null/empty/blank before Number()
                // so Number(null)/Number("") do not look like a valid free product.
                // Negative prices are invalid in this domain — clamp to 0 via safePrice.
                const rawPrice = this.prosubDetails?.price;
                const trimmedPrice = typeof rawPrice === "string" ? rawPrice.trim() : rawPrice;
                const numericPrice =
                  typeof trimmedPrice === "number"
                    ? trimmedPrice
                    : typeof trimmedPrice === "string" && trimmedPrice !== ""
                      ? Number(trimmedPrice)
                      : NaN;
                const hasNumericPrice = Number.isFinite(numericPrice) && numericPrice >= 0;
                const safePrice = hasNumericPrice ? numericPrice : 0;
                dataLayer.push({
                  event: "prosub_details_loaded",
                  Prosub_Id: requestedProsubId,
                  prosub_price: safePrice,
                  prize: safePrice,
                  prosub_price_valid: hasNumericPrice,
                });
              },
              (err) => {
                if (this.lastPartialRegDefaultsProsubId !== requestedProsubId) {
                  return;
                }
                this.authService.errorHandlerLogs('[GTM] Error loading prosub details on partial-registration:', err);
                dataLayer.push({
                  event: "prosub_details_error",
                  Prosub_Id: requestedProsubId,
                  prosub_price: 0,
                  prize: 0,
                  prosub_price_valid: false,
                });
              }
            );
        } else {
          // For all other matching routes, push immediately (no price data needed)
          dataLayer.push({
            Prosub_Id: prosubId,
          });
        }

        // Handle successful-payment route
        if (this.router.url.includes("successful-payment")) {
          // Load prosub details first before sending fbq events
          this.getLSWProsubDetails(prosubId)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(
            (prosubDetails: Data) => {
              this.prosubDetails = prosubDetails?.data();
              
              // Only execute fbq tracking after prosubDetails is loaded
              if (typeof fbq !== "undefined" && fbq && this.prosubDetails) {
                this.commonService.getUid("gtm-segment-service")
                  .pipe(takeUntilDestroyed(this.destroyRef))
                  .subscribe(userId => {
                  if (!userId) {
                    console.warn('[GTM] Skipping Facebook Pixel tracking - no userId available');
                    return;
                  }
                  
                  const offerId = this.prosubDetails?.offer_id ?? null;
                  if (!offerId) {
                    console.warn('[GTM] offer_id is null/undefined, skipping Facebook Pixel tracking');
                    return;
                  }

                  fbq("track", "Purchase", {
                    Offer_Id: offerId,
                    Prosub_Id: prosubId || this.prosubDetails?.prosub_id,
                    value: 123,
                    currency: "rs",
                    p_type: "credit",
                    customer_id: userId,
                  });

                  fbq("track", "Subscribe", {
                    value: this.prosubDetails?.price,
                    currency: "GBP",
                  });
                });
              } else {
                console.warn('[GTM] Skipping Facebook Pixel tracking - fbq not defined or prosubDetails not loaded');
              }
            },
            (err) => {
              console.error("Error loading prosub details for successful-payment:", err);
            }
          );
        }
      }
    });
  }



  getLSWProsubDetails(id: string) {
    if (!id) return of(null); // Return null if no id is provided

    return this.catchService.isOnline$.pipe(
      switchMap((isOnline) => {
        if (!isOnline) {
          console.error("No internet connection.");
          return of(null); // Return null observable if offline
        }
        // Reference the document for the specified prosub_id
        const docRef = doc(this.firestore, `/general_modules/prosub_id/${id}/details`);
        // Fetch the document data using getDoc() and convert to an observable
        return from(getDoc(docRef)).pipe(
          this.catchService.retryWhen,
          this.catchService.catchOfflineError
        );
      }),
      shareReplay(1),
      catchError((error) => {
        console.error("Error in getLSWProsubDetails:", error);
        return of(null); // Return null on error to avoid breaking the chain
      })
    );
  }

  formPaymentDataForTopUp(offer: any) {
    return {
      prosub_id: offer?.prosub_id,
      phone: offer?.phone,
      prize: offer?.price,
      offers: offer,
      checkout: true,
      draw: "",
      game: "",
      line: "",
      offer_id: offer?.offer_id,
      real_name: "",
      page: "store",
      TP1: this.prosub && this.prosub.params && this.prosub.params.TP1 ? this.prosub.params.TP1?.toLowerCase() : "",
      TP2: this.prosub && this.prosub.params && this.prosub.params.TP2 ? this.prosub.params.TP2?.toLowerCase() : "",
      TP3:
        this.prosub && this.prosub.params
          ? this.prosub.params.TP3
            ? this.prosub.params.TP3?.toLowerCase()
            : this.prosub.params[
              Object.keys(this.prosub.params)?.find((key) => key?.toLowerCase() === "tp3")
            ]?.toLowerCase() ?? ""
          : "",
      T_ID:
        this.prosub && this.prosub.params
          ? this.prosub.params.T_ID
            ? this.prosub.params.T_ID?.toLowerCase()
            : this.prosub.params[
              Object.keys(this.prosub.params)?.find((key) => key?.toLowerCase() === "t_id")
            ]?.toLowerCase() ?? ""
          : "",
      other:
        this.prosub && this.prosub.params && this.prosub.params.Other ? this.prosub.params.Other?.toLowerCase() : "",
      vc: this.vc,
    };
  }

  async saleRedirectionByProsub(prosub_id: Data, vc: Data, matDialog: Data) {
    if (!this.prosubDetails || !this.prosubDetails.optional_page) {
      // this.getGlobalProsub().subscribe((d: any) => {});
      this.prosub = localStorage.getItem("prosub_id");
      if (!this.prosub) {
        this.prosub = await this.getGlobalProsub().toPromise();
      }
      localStorage.setItem("prosub_id", this.prosub);

      if (this.prosubDetails) {
        this.prosubDetails = this.getLSWProsubDetails(this.prosub).toPromise();
      }

      // this.getLSWProsubDetails(this.prosub).subscribe((prosubDetails) => { });
      // this.prosubDetails = prosubDetails.data();
      this.userProduct$.next(this.formPaymentDataForTopUp(this.prosubDetails));
      this.callINT1CommonMethod(matDialog, vc, prosub_id);
      //this.loading.dismiss();
    } else {
      this.callINT1CommonMethod(matDialog, vc, prosub_id);
      this.userProduct$.next(this.formPaymentDataForTopUp(this.prosubDetails));
      this.loading.dismiss();
    }
  }
  private callINT1CommonMethod(matDialog: Data, vc: Data, prosub_id: Data) {
    this.callInt1()
      .then(async (resInt: string) => {
        if (vc && resInt !== "/free-reg-step4c") {
          this.router.navigate([resInt], {
            queryParams: { prosub_id: prosub_id, vc: vc, ...this.queryParamsToNavigate },
          });
        } else {
          if (resInt === "/free-reg-step4c") {
            this.AuthPartialRegService.partial_Reg(prosub_id, "", "").subscribe((res) => {
              this.router.navigate([resInt], {
                queryParams: { prosub_id: prosub_id, ...this.queryParamsToNavigate },
                state: res,
              });
            });
            return;
          }

          this.router.navigate([resInt], {
            queryParams: { prosub_id: prosub_id, ...this.queryParamsToNavigate },
          });
        }

        //this.loading.dismiss();
      })
      .catch((error) => {
        this
          .getConstantsMsg("1014")
          .then((translatedMsg) => {
            const defaultMsg = {
              message: {
                line_1: "Something went wrong",
                line_2: "",
              },
              style: {
                icon: "tick circle error.svg",
                type: "Error",
              },
            };
            this.showToastMessage(translatedMsg, defaultMsg);
          })
          .catch((error) => {
            return error;
          });

        this.loading.dismiss();

        return error;
      });
  }

  public showToastMessage(translatedMsg, defaultMsg): void {
    const message = translatedMsg?.message ? translatedMsg?.message : defaultMsg?.message;
    const icon = translatedMsg?.style?.icon ? translatedMsg?.style?.icon : defaultMsg?.style?.icon;
    const snackType = translatedMsg?.style?.type ? translatedMsg?.style?.type : defaultMsg?.style?.type;
    const duration = translatedMsg?.style?.duration ? translatedMsg?.style?.duration : 2000;
    this.snackBar.openFromComponent(MyToastbarComponent, {
      duration: duration,
      horizontalPosition: "center",
      verticalPosition: "top",
      panelClass: "toast-for-syndicate",
      data: { message: message, duration: duration, icon: icon, state: snackType },
    });
  }

  public getConstantsMsg(msgCode: any): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      try {
        // Use fallback message if offline or in login flow
        if (!navigator.onLine || this.isInLoginFlow) {
          const fallbackMsg = this.getFallbackMessage(msgCode);
          if (fallbackMsg) {
            resolve(fallbackMsg);
            return;
          }
        }

        const msg$ = this.commonService.getAngularFireStore(`/Global_lookup/toast_message/list/${msgCode}`, "get");

        if (!msg$) {
          resolve(null);
          return;
        }

        msg$.subscribe({
          next: (snapshot: any) => {
            if (!snapshot?.exists()) {
              resolve(null);
              return;
            }

            const msgData = snapshot.data();

            if (!msgData) {
              resolve(null);
              return;
            }

            const userLang = this.selectedlanguage$?.value;
            const fallbackLang = CommonVariable?.preferredLanguage;

            const toastMsg =
              msgData?.message?.[userLang] ??
              msgData?.message?.[fallbackLang] ??
              "";

            const toastMsgData = {
              message: toastMsg,
              style: msgData?.style || {},
            };

            resolve(toastMsgData);
          },
          error: (err) => {
            console.error("Error fetching toast message:", err);
            resolve(null); // fallback to null on error
          },
        });
      } catch (err) {
        reject(err);
      }
    });
  }
  private getFallbackMessage(msgCode: string): Data {
    return this.commonService.fallbackMessages[msgCode] || null;
  }

  trackInlineNotifications(msg: string, body: Data) {
    try {
      analytics.track(msg, {
        ...body,
      });
    } catch (error) {
      return error;
    }
  }

  callInt1(prosubId: string = ""): Promise<string> {
    return new Promise((resolve, reject) => {
      this.commonService.getUid("gtm-segment-service").pipe(take(1)).subscribe({
        next: (uid) => {
          if (!uid) {
            return reject("No userId found");
          }

          const requestData = {
            userId: uid, // ✅ from CommonService
            prosub_id: prosubId || localStorage.getItem("prosub_id"),
            TP1: this.TP1 || "",
            TP2: this.TP2 || "",
            TP3: this.TP3 || "",
            T_ID: this.T_ID || "",
          };

          this.getCallInt1(requestData)
            .pipe(take(1))
            .subscribe(
              (res: Data) => {
                if (res?.status === "success") {
                  resolve(res.post_name || "");
                } else {
                  resolve("");
                }
              },
              () => reject(false)
            );
        },
        error: () => reject("Error fetching uid"),
      });
    });
  }


  // getPostNameFromInt1(requestData) {
  //   return this.angularFireFunctions.httpsCallable("interstitialOne")(requestData);
  // }
  getCallInt1(requestData) {
    return this.getV2CloudFunction("callinterstitialonev2", requestData);
    // return this.angularFireFunctions.httpsCallable("callinterstitialone")(requestData);
  }
  public getV2CloudFunction(CloudFunctionName: string, payload: Data | string, options: Data = {}) {
    try {
      // Check if browser is offline using navigator.onLine
      if (!navigator.onLine) {
        console.error(`Network is offline, cannot call cloud function: ${CloudFunctionName}`);
        return throwError(new Error('OFFLINE_ERROR'));
      }

      const functions = getFunctions();

      functions.region = GTMSagmentService.FIREBASE_REGION;
      return defer(() => {
        return from(httpsCallable(functions, CloudFunctionName, options)(payload));
      }).pipe(
        map((res: Data) => {
          return res?.data;
        }),
        retryWhen((errors) =>
          errors.pipe(
            scan((retryCount, error) => {
              // Check if error is due to network connectivity
              if (error && (error.message?.toLowerCase().includes('offline') ||
                error.message?.toLowerCase().includes('network'))) {
                console.error('Network error detected, waiting for connectivity...');
                if (!navigator.onLine) {
                  throw new Error('OFFLINE_ERROR');
                }
              }
              if (retryCount >= 3) {
                throw error;
              }
              return retryCount + 1;
            }, 0),
            delay(1000) // Delay for retry in milliseconds
          )
        ),
        catchError((error) => {
          if (error && error.message === 'OFFLINE_ERROR') {
            return throwError(new Error('OFFLINE_ERROR'));
          }
          return throwError(new Error(`Error in ${CloudFunctionName}, Reason: ${error}`));
        })
      );
    } catch (error) {
      return throwError(error);
    }
  }
  //Set clevertap identity with the user details.
  setUserIdentify(user: any) {
    try {
      if (!user || typeof analytics === "undefined") return;

      let phoneNumber = user?.msn || "";

      // Ensure phoneNumber is a string
      if (typeof phoneNumber !== "string") {
        phoneNumber = String(phoneNumber);
      }

      // Remove leading 0
      if (phoneNumber.startsWith("0")) {
        phoneNumber = phoneNumber.substring(1);
      }

      // Append country code if missing
      if (!phoneNumber.startsWith(user?.country_code)) {
        phoneNumber = user?.country_code + phoneNumber;
      }

      // ✅ Get userId from CommonService instead of localStorage
      this.commonService.getUid("segment-service").pipe(take(1)).subscribe({
        next: (uid) => {
          if (!uid) return;

          analytics?.identify(uid, {
            email: user?.email,
            name: user?.name,
            phone: phoneNumber ? "+" + phoneNumber : "",
          });
        },
        error: (err) => {
          console.error("[setUserIdentify] Failed to get uid:", err);
        }
      });
    } catch (error) {
      console.error("[setUserIdentify] Error:", error);
      return error;
    }
  }

  GTMPopupInitialization(msg?: any, body?: any) {
    if (typeof analytics === "undefined") return;

    this.commonService.getUid("gtm-popup-init").pipe(take(1)).subscribe({
      next: (uid) => {
        if (!uid) return;

        analytics?.identify(uid);

        analytics?.track(msg, {
        data: msg,
        ...(body != null ? { message: body } : {}),
      });
      },
      error: (err) => {
        console.error("[GTMPopupInitialization] Failed to get uid:", err);
      }
    });
  }


  getGlobalProsub(): Observable<any> {
    return this.catchService.isOnline$.pipe(
      switchMap((isOnline) => {
        if (!isOnline) {
          console.error("No internet connection.");
          return of(null); // Return null if offline
        }
        // Reference the Firestore document
        const systemReferenceDocRef = doc(this.firestore, 'general_modules/system_reference');
        // Use onSnapshot for real-time updates
        return new Observable((subscriber) => {
          const unsubscribe = onSnapshot(
            systemReferenceDocRef,
            (snapshot) => {
              const data = snapshot.data();
              subscriber.next(data);
            },
            (error) => {
              subscriber.error(error);
            }
          );
          return () => unsubscribe(); // Cleanup subscription when complete
        });
      }),
      shareReplay(1), // Cache the response for reuse
      map((data) => data || null), // Ensure null safety in mapping
      catchError((error) => {
        console.error("Error in getGlobalProsub:", error);
        return of(null); // Return null observable in case of error
      })
    );
  }

  getProsubDetails(prosub_id: string) {
    if (!prosub_id) return of(null); // Return null if prosub_id is not provided
    return this.catchService.isOnline$.pipe(
      switchMap((isOnline) => {
        if (!isOnline) {
          console.error("No internet connection.");
          return of(null); // Return null observable if offline
        }
        // Reference the document for the specified prosub_id
        const docRef = doc(this.firestore, `/general_modules/prosub_id/${prosub_id}/details`);
        // Fetch the document data using getDoc() and convert it to an observable
        return from(getDoc(docRef)).pipe(
          this.catchService.retryWhen,
          this.catchService.catchOfflineError
        );
      }),
      shareReplay(1), // Cache the response for reuse
      catchError((error) => {
        console.error("Error fetching Prosub details:", error);
        return of(null); // Return null on error to prevent breaking the chain
      })
    );
  }
  getCappingStatus(offerId: string) {
    if (!offerId) return of(null); // Return null if offerId is not provided

    return this.catchService.isOnline$.pipe(
      switchMap((isOnline) => {
        if (!isOnline) {
          console.error("No internet connection.");
          return of(null); // Return null observable if offline
        }

        // Get userId from commonService instead of localStorage
        return this.commonService.getUid("capping-status").pipe(
          take(1),
          switchMap((userId) => {
            if (!userId) {
              console.error("Invalid User ID.");
              return of(null); // Return null if the User ID is invalid
            }

            // Reference the document for the specific offer capping
            const docRef = doc(this.firestore, `users/${userId}/offer_capping/${offerId}`);

            // Fetch the document data using getDoc() and convert to an observable
            return from(getDoc(docRef)).pipe(
              this.catchService.retryWhen,
              this.catchService.catchOfflineError
            );
          })
        );
      }),
      shareReplay(1), // Cache the response for reuse
      catchError((error) => {
        console.error("Error fetching capping status:", error);
        return of(null); // Return null on error to prevent breaking the chain
      })
    );
  }


  getOfferCardsDetails() {
    return this.catchService.isOnline$.pipe(
      switchMap((isOnline) => {
        if (!isOnline) {
          console.error("No internet connection.");
          return of(null); // Return null observable if offline
        }

        // Reference the Firestore document
        const offerCardDocRef = doc(this.firestore, "/general_modules/free_reg_step4a_offer_card_new");

        // Fetch the document data using getDoc() and convert to an observable
        return from(getDoc(offerCardDocRef)).pipe(
          this.catchService.retryWhen,
          this.catchService.catchOfflineError
        );
      }),
      shareReplay(1), // Cache the response for reuse
      catchError((error) => {
        console.error("Error fetching Offer Cards details:", error);
        return of(null); // Return null on error to avoid breaking the chain
      })
    );
  }
  GTM_CJ_site_pages(page: any) {
    this.commonService.getUid("gtm-cj-site-pages").pipe(take(1)).subscribe((userId) => {
      if (!userId) return;

      const email = localStorage.getItem("email"); // keep as is

      dataLayer.push({
        event: "cj_site_page",
        pageType: page,
        c_customer_id: userId,
        marketing_Channel: "Affiliate",
        order_Subtotal: "0.00",
        Email_Hash: email,
      });
    });
  }


  GTM_CJ_conversion(
    amount: any,
    currency: any,
    offer_id: any,
    customer_status: string,
    customer_country: string,
    p_type: any,
    prosub_id: any,
    customer_purchase_id: string,
    purchaseContext?: PaymentPurchaseContext
  ) {
    // Push immediately — do not wait on getUid auth gates (can take several seconds
    // or fail), which previously caused payment_success to be dropped after UI success.
    const resolvedUserId = this.resolveSyncCustomerId();
    const resolvedContext: PaymentPurchaseContext = mergePaymentPurchaseContext(
      purchaseContext,
      {
        purchaseDetail: this.userProduct$?.getValue?.() ?? this.userProduct$?.value,
        altprosubDetails: this.prosubDetails,
      }
    );
    const resolved = resolvePaymentAmountAndOffer(
      amount,
      offer_id,
      currency,
      resolvedContext
    );
    const cjSubTotal = resolved.subTotal;
    const cjCurrency = resolved.currency || "GBP";
    const resolvedProsubId = firstPresentString(
      prosub_id,
      resolvedContext.altprosubDetails?.prosub_id,
      resolvedContext.purchaseDetail?.prosub_id
    );
    const resolvedOfferId = resolved.offerId;
    const resolvedPurchaseId =
      customer_purchase_id !== undefined &&
      customer_purchase_id !== null &&
      String(customer_purchase_id) !== "" &&
      String(customer_purchase_id) !== "null"
        ? String(customer_purchase_id)
        : "";

    const dedupeKey =
      `${resolvedProsubId}|${cjSubTotal}|${resolvedOfferId}|${resolvedPurchaseId}`;
    if (dedupeKey && this.lastPaymentSuccessDedupeKey === dedupeKey) {
      return;
    }
    this.lastPaymentSuccessDedupeKey = dedupeKey;

    const email = localStorage.getItem("email"); // keep email as is
    let emailHash = null;
    if (email) {
      // Hash the email using SHA-256
      emailHash = CryptoJS.SHA256(email).toString(CryptoJS.enc.Hex);
    }

    const cj_products = [
      {
        item_id: resolvedOfferId,
        item_name: resolvedOfferId,
        price: cjSubTotal,
        quantity: 1,
      },
    ];

    const paymentSuccessPayload = {
      event: "payment_success",
      subTotal: cjSubTotal,
      coupon: "",
      discount: "0.00",
      currency: cjCurrency,
      orderid: resolvedOfferId || offer_id,
      customer_status: customer_status,
      customer_country: customer_country,
      customer_purchase_id: resolvedPurchaseId,
      email_sha256: emailHash,
      // Ecommerce vars required by GTM / AppLovin purchase mapping
      prosub_id: resolvedProsubId,
      cj_currency: cjCurrency,
      cj_subTotal: cjSubTotal,
      cj_products: cj_products,
      c_customer_id: resolvedUserId,
      products: [
        {
          productTitle: resolvedUserId, // now comes from commonService
          productType: p_type,
          productBrand: resolvedProsubId,
          productSku: resolvedOfferId,
          productPrice: cjSubTotal,
          productQuantity: "1",
          productDiscount: "0.00",
          // Also expose AppLovin item shape on products in case GTM maps items from it
          item_id: resolvedOfferId,
          item_name: resolvedOfferId,
          price: cjSubTotal,
          quantity: 1,
        },
      ],
    };

    this.persistPendingPaymentSuccess(paymentSuccessPayload);

    if (!window.dataLayer) {
      window.dataLayer = [];
    }

    // Push both events in the same turn so a 4C/profile redirect cannot drop CJpurchase.
    window.dataLayer.push(paymentSuccessPayload);
    window.dataLayer.push({
      event: "CJpurchase",
    });

    // HATDEV-100772: PostHog mirror — Mixpanel User_Purchased property parity (no email)
    this.posthogService.identify(resolvedUserId);
    this.posthogService.capture(
      "payment_succeeded",
      buildPaymentSucceededProps({
        currency: cjCurrency,
        subTotal: cjSubTotal,
        offerId: resolvedOfferId,
        prosubId: resolvedProsubId,
        customerStatus: customer_status,
        customerCountry: customer_country,
        customerPurchaseId: resolvedPurchaseId,
        productType: p_type,
        customerId: resolvedUserId,
        context: resolvedContext,
      })
    );
  }

  private lastPaymentSuccessDedupeKey: string | null = null;
  private readonly pendingPaymentSuccessStorageKey = "gtm_pending_payment_success";

  /** Offer price may be a number, numeric string, or a currency map like { GBP: 1 }. */
  private coercePurchaseAmount(amount: any): number {
    return firstPurchaseAmount(amount);
  }

  private persistPendingPaymentSuccess(payload: Record<string, unknown>): void {
    try {
      sessionStorage.setItem(this.pendingPaymentSuccessStorageKey, JSON.stringify(payload));
    } catch {
      // ignore storage quota / private-mode failures
    }
  }

  private replayPendingPaymentSuccessIfNeeded(): void {
    const url = this.router?.url || "";
    const isPostPaymentRoute =
      url.includes("free-reg-step4c") ||
      url.includes("free-reg-step-4c") ||
      url.includes("free-reg-complete-profile") ||
      url.includes("successful-payment");
    if (!isPostPaymentRoute) {
      return;
    }

    try {
      const raw = sessionStorage.getItem(this.pendingPaymentSuccessStorageKey);
      if (!raw) {
        return;
      }
      sessionStorage.removeItem(this.pendingPaymentSuccessStorageKey);
      const payload = JSON.parse(raw);
      if (!payload || payload.event !== "payment_success") {
        return;
      }

      if (!window.dataLayer) {
        window.dataLayer = [];
      }

      const purchaseId = String(payload.customer_purchase_id || "");
      const alreadyPushed = window.dataLayer.some(
        (entry: { event?: string; customer_purchase_id?: string }) =>
          entry?.event === "payment_success" &&
          String(entry?.customer_purchase_id || "") === purchaseId
      );
      if (alreadyPushed) {
        return;
      }

      window.dataLayer.push(payload);
      window.dataLayer.push({
        event: "CJpurchase",
      });
    } catch {
      // ignore parse / storage failures
    }
  }

  private resolveSyncCustomerId(): string {
    try {
      const authUid = getAuth()?.currentUser?.uid;
      if (authUid) {
        return String(authUid);
      }
    } catch {
      // ignore
    }
    try {
      const fromStorage =
        this.safeLocalStorage.getItem("uid") ||
        this.safeLocalStorage.getItem("user_id") ||
        localStorage.getItem("uid") ||
        localStorage.getItem("user_id") ||
        "";
      if (fromStorage && fromStorage !== "null") {
        return String(fromStorage);
      }
    } catch {
      // ignore storage access errors
    }
    return "";
  }


  HotJarIdentify(user: Data, ip: Data) {
    this.commonService.getUid("hotjar-identify").pipe(take(1)).subscribe((userId) => {
      if (!userId) return;

      dataLayer.push({
        event: "hotJarIdentify",
        hj_name: user?.name,
        hj_user_id: userId, // now from commonService
        hj_email: user?.email,
        hj_ip_address: ip,
        hj_language: user?.language_code,
        hj_user_state: user?.onboard_state,
        hj_segment: user?.pwa_segment_status,
        hj_registered_since: user?.timestamp?.toDate().toDateString(),
      });
    });
  }


  likeFeedCard(user: Data, cardDetails: Data) {
    this.commonService.getUid("like-feed-card").pipe(take(1)).subscribe((userId) => {
      if (!userId) return;

      dataLayer.push({
        event: "like_feed_card",
        user_name: user?.name,
        user_id: userId, // from commonService
        user_email: user?.email,
        user_language: user?.language_code,
        user_msn: user?.msn,
        wp_user_id: user?.wp_user_id,
        card_id: cardDetails?.card_id,
        card_type: cardDetails?.card_type,
        card_name: cardDetails?.card_name,
      });
    });
  }


  // GA4 track event for game play
  trackGamesView(game: string) {
    this.commonService.getUid("track-games-view").pipe(take(1)).subscribe((userId) => {
      if (!userId) return;

      dataLayer.push({
        event: "track_games_view",
        user_id: userId,   // ✅ now tracked properly with UID
        game: game,
      });
    });
  }


  FB_advanced_matching(
    phone: Data,
    email: Data,
    first_name: Data,
    last_name: Data,
    dob: Data
  ) {
    this.commonService.getUid("FB_advanced_matching").pipe(take(1)).subscribe((userId) => {
      if (!userId) return;

      dataLayer.push({
        event: "advanced_matching",
        c_customer_id: userId,  // ✅ pulled from commonService, not localStorage
        phone: phone,
        em: email,
        fn: first_name,
        ln: last_name,
        db: dob,
      });
    });
  }

  GTM_account_created(phoneNumber?: string) {
    const timeout = 10000;
    setTimeout(() => {
      this.commonService.getUid("GTM_account_created").pipe(take(1)).subscribe((userId) => {
        if (!userId) return;

        dataLayer.push({
          event: "registration_success",
          c_customer_id: userId,
          msn: phoneNumber,
        });

        // HATDEV-100772: PostHog mirror of GTM registration_success (deduped with signup paths)
        this.posthogService.emitRegistrationCompleted("gtm_registration_success", userId);
      });
    }, timeout);
  }


  Clevertap_profile_push(msn: string) {
    const timeDelay = 10000;
    setTimeout(() => {
      this.commonService.getUid("Clevertap_profile_push").pipe(take(1)).subscribe((userId) => {
        if (!userId) return;

        msn = msn.replace("+", "");
        this.clevertapService.sendClevertapData(userId, {
          msn,
          email: localStorage.getItem("email") === null ? "" : localStorage.getItem("email"),
        });
      });
    }, timeDelay);
  }


  GTM_4c_page() {
    this.commonService.getUid("GTM_4c_page").pipe(take(1)).subscribe((userId) => {
      if (!userId) return;
      dataLayer.push({
        event: "4c_page",
        c_customer_id: userId,
      });
    });
  }


  HotJarEvent(event: string) {
    if (event === "user_login" || event === "forgot_password") {
      this.HotJarEventEmptyUser(event);
    }

    if (event === "user_played_game") {
      this.clarityService.event("play_game");
    }

    this.commonService.getUid("HotJarEvent").pipe(take(1)).subscribe((userId) => {
      if (!userId) return;
      if (!window.dataLayer) {
        window.dataLayer = [];
      }
      window.dataLayer.push({
        event: "hotjar_event",
        hj_event: event,
      });
    });
  }

  // This method has been used to call HotJar Event Empty User case
  HotJarEventEmptyUser(event: string) {
    // Ensure window.dataLayer is initialized
    if (!window.dataLayer) {
      window.dataLayer = [];
    }

    // Push event data to dataLayer
    window.dataLayer.push({
      event: "hotjar_event",
      hj_event: event,
    });
  }


  GTMPopupInitializationForInbox(msg: string, body?: Data) {
    if (typeof analytics === "undefined") return;

    this.commonService.getUid("GTMPopupInitializationForInbox").pipe(take(1)).subscribe((userId) => {
      if (!userId) return;

      analytics?.identify(userId);

      analytics?.track(msg, {
        data: msg,
        ...(body != null ? { message: body } : {}),
      });
    });
  }


  /**
   * Tracks an analytics event for a GTM popup initialization in an inbox.
   * @param msg The message or event name to track.
   * @param body Optional additional data to include in the event.
   */
  GTMPopupInitializationForInboxNew(msg: string, body?: Data) {
    try {
      if (typeof analytics === "undefined") return;

      this.commonService.getUid("GTMPopupInitializationForInboxNew")
        .pipe(take(1))
        .subscribe((userId) => {
          if (!userId) return;

          analytics?.identify(userId);

          analytics?.track(msg, {
            ...body,
          });
        });
    } catch (error) {
      return;
    }
  }

  // Initializes a GTM event popup with a given message and optional data body.
  GTMEventPopupInitialization(msg: string, body?: Data) {
    if (typeof analytics === "undefined") return;

    this.commonService.getUid("GTMEventPopupInitialization")
      .pipe(take(1))
      .subscribe((userId) => {
        if (!userId) return;

        analytics?.identify(userId);

        analytics?.track(msg, {
          ...body,
        });
      });
  }


  // This method has been used to call GTM Event tracking case
  public trackCreditTopUp() {
    analytics.track("product_credit_topup_banner", {
      TP1: "product_credit_topup_banner",
    });
  }

  public trackGameChallenge(eventName: string, eventProperty: Data) {
    analytics.track(eventName, eventProperty);
  }

  public trackBannerClick(eventName: string, eventProperty: Data) {
    analytics.track(eventName, eventProperty);
  }
}

